import { Select } from '@wanjianshi/admin-ui';
import type { SelectProps } from 'antd';
import type { ReactNode } from 'react';
import { useCallback, useEffect, useRef, useState } from 'react';

const DEFAULT_DEBOUNCE_MS = 350;

type RemoteSelectValue = string | number;

export interface AdminRemoteSelectOption<
  TValue extends RemoteSelectValue,
  TRecord,
> {
  value: TValue;
  label: ReactNode;
  disabled?: boolean;
  record?: TRecord;
}

export interface AdminRemoteSelectProps<
  TRecord,
  TValue extends RemoteSelectValue = string,
> extends Omit<
    SelectProps<TValue>,
    'filterOption' | 'loading' | 'notFoundContent' | 'onSearch' | 'options'
  > {
  loadOptions: (keyword: string, signal: AbortSignal) => Promise<TRecord[]>;
  mapOption: (record: TRecord) => AdminRemoteSelectOption<TValue, TRecord>;
  initialOption?: AdminRemoteSelectOption<TValue, TRecord> | null;
  debounceMs?: number;
  onSelectRecord?: (record: TRecord, value: TValue) => void;
}

function isSearchableKeyword(keyword: string): boolean {
  return keyword.length > 0 && (keyword.length >= 2 || /^\d+$/.test(keyword));
}

function valueKey(value: RemoteSelectValue): string {
  return `${typeof value}:${String(value)}`;
}

/**
 * 后台统一远程选择器。
 *
 * 空关键词不会请求；输入时会防抖、取消旧请求，并阻止旧响应覆盖新结果。
 * 业务页面只负责提供查询方法和展示映射，不再各自维护搜索状态。
 */
export function AdminRemoteSelect<
  TRecord,
  TValue extends RemoteSelectValue = string,
>({
  loadOptions,
  mapOption,
  initialOption = null,
  debounceMs = DEFAULT_DEBOUNCE_MS,
  onSelectRecord,
  onSelect,
  className,
  ...selectProps
}: AdminRemoteSelectProps<TRecord, TValue>) {
  const [options, setOptions] = useState<Array<AdminRemoteSelectOption<TValue, TRecord>>>(
    initialOption ? [initialOption] : [],
  );
  const [loading, setLoading] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);
  const [searchFailed, setSearchFailed] = useState(false);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const controllerRef = useRef<AbortController | null>(null);
  const sequenceRef = useRef(0);
  const recordsRef = useRef(new Map<string, TRecord>());

  const cancelPendingSearch = useCallback(() => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
    controllerRef.current?.abort();
    controllerRef.current = null;
  }, []);

  useEffect(() => {
    cancelPendingSearch();
    recordsRef.current.clear();
    if (initialOption?.record) {
      recordsRef.current.set(valueKey(initialOption.value), initialOption.record);
    }
    setOptions(initialOption ? [initialOption] : []);
    setLoading(false);
    setHasSearched(false);
    setSearchFailed(false);
  }, [cancelPendingSearch, initialOption]);

  useEffect(() => cancelPendingSearch, [cancelPendingSearch]);

  const search = useCallback((rawKeyword: string) => {
    const keyword = rawKeyword.trim();
    cancelPendingSearch();
    const sequence = ++sequenceRef.current;
    setSearchFailed(false);

    if (!isSearchableKeyword(keyword)) {
      recordsRef.current.clear();
      if (initialOption?.record) {
        recordsRef.current.set(valueKey(initialOption.value), initialOption.record);
      }
      setOptions(initialOption ? [initialOption] : []);
      setLoading(false);
      setHasSearched(false);
      return;
    }

    timerRef.current = setTimeout(async () => {
      const controller = new AbortController();
      controllerRef.current = controller;
      setLoading(true);
      setHasSearched(true);
      try {
        const records = await loadOptions(keyword, controller.signal);
        if (controller.signal.aborted || sequence !== sequenceRef.current) return;

        const mapped = records.map(record => ({ ...mapOption(record), record }));
        const uniqueOptions = new Map<string, AdminRemoteSelectOption<TValue, TRecord>>();
        if (initialOption) uniqueOptions.set(valueKey(initialOption.value), initialOption);
        mapped.forEach(option => uniqueOptions.set(valueKey(option.value), option));

        recordsRef.current = new Map(
          mapped.map(option => [valueKey(option.value), option.record as TRecord]),
        );
        if (initialOption?.record) {
          recordsRef.current.set(valueKey(initialOption.value), initialOption.record);
        }
        setOptions(Array.from(uniqueOptions.values()));
      } catch {
        if (controller.signal.aborted || sequence !== sequenceRef.current) return;
        setSearchFailed(true);
        setOptions(initialOption ? [initialOption] : []);
      } finally {
        if (!controller.signal.aborted && sequence === sequenceRef.current) {
          setLoading(false);
          controllerRef.current = null;
        }
      }
    }, debounceMs);
  }, [cancelPendingSearch, debounceMs, initialOption, loadOptions, mapOption]);

  const notFoundContent = loading
    ? '正在搜索…'
    : searchFailed
      ? '搜索失败，请稍后重试'
      : hasSearched
        ? '未找到匹配数据'
        : '请输入至少 2 个字符搜索';

  return (
    <Select<TValue>
      {...selectProps}
      showSearch
      filterOption={false}
      defaultActiveFirstOption={false}
      loading={loading}
      options={options}
      notFoundContent={notFoundContent}
      className={['wjs-admin-remote-select', className].filter(Boolean).join(' ')}
      onSearch={search}
      onSelect={(value, option) => {
        const selectedValue = value as TValue;
        const record = recordsRef.current.get(valueKey(selectedValue));
        if (record) onSelectRecord?.(record, selectedValue);
        onSelect?.(value, option);
      }}
    />
  );
}
